<script setup lang="ts">
import { PropType, inject, Ref, computed, ref, watch } from 'vue';
import { TasksListWrapper } from './';
import { DragModel, TirPmDraggable } from '../../tir-pm-kanban';
import { TaskCard } from './task-card';
import { TaskModel } from '../../tasks-kanban/models';
import { TasksKanbanGrouppingTypeEnum } from '../../tasks-kanban/enums';
import { ListItemModel } from './../models';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

/**Параметры */
const props = defineProps({
  /**Задача */
  task: { type: Object as PropType<DragModel<TaskModel>> },
  /**Тип группировки */
  grouppingType: { type: Number as PropType<TasksKanbanGrouppingTypeEnum> },
  /**Уровень вложенности */
  deepLevel: { type: Number, default: 0 },
  /**Размер имени задачи */
  nameWidth: { type: String },
  /**Размеры колонок списка */
  listColumns: { type: Array as PropType<ListItemModel[]> },
  /**Видимость данных группировки в карточке */
  visibleTaskGroupData: { type: Boolean, default: false },
  /**С какого уровня будут видны данные группировки в карточке */
  visibleTaskGroupDataStartLevel: { type: Number, default: 0 },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
});
/**События компонента */
const emit = defineEmits<{
  /**Перетаскивание элемента */
  (e: 'dragComponentOnDrag', isDrag: boolean, data: DragModel<any>): void;
  /**Наведение на элемент */
  (e: 'dragComponentOnOver', isOver: boolean, data: DragModel<any>): void;
  /**Дроп элемента */
  (e: 'dragComponentOnDrop', dropArea: DragModel<any>): void;
  /**Загрузка подзадач */
  (e: 'loadSubtasks', task: TaskModel): void;
  /**Открыть задачу */
  (e: 'open', taskId: number): void;
  /**Удалить задачу */
  (e: 'removeTask', taskId: number);
}>();

/**Список id задач */
const tempTasksIds = [];
/**Виден ли список подзадач из текущей очереди */
const isCurrentSubtasksVisible = ref(false);
/**Виден ли список подзадач из других очередей */
const isOtherSubtasksVisible = ref(false);

/**Перетаскиваемый элемент */
const currentDragging = inject<Ref<DragModel<TaskModel>>>('currentDragging');
/**Элемент над которым находится перетаскиваемый объект */
const overDragging = inject<Ref<DragModel<TaskModel>>>('overDragging');
/**Id очереди */
const queueId = inject('queueId') as Ref<string>;

/**Перетаскивание разрешено */
const dragAllowed = computed(() => {
  return [
    TasksKanbanGrouppingTypeEnum.Status,
    TasksKanbanGrouppingTypeEnum.Priority,
    TasksKanbanGrouppingTypeEnum.Type,
    TasksKanbanGrouppingTypeEnum.None,
  ].includes(props.grouppingType);
});
/**Над этим элементом находится перетаскиваемый объект */
const isOver = computed(() => {
  return (
    !!currentDragging.value &&
    currentDragging.value.Data.Id != props.task.Data.Id &&
    overDragging?.value?.Data.Id === props.task?.Data.Id &&
    checkAllowOver()
  );
});
/**Текущая задача перетаскивается */
const isTaskDragging = computed(() => currentDragging.value?.Data?.Id == props.task.Data.Id);
/**Видны ли подзадачи */
const isSubtasksVisible = computed(
  () =>
    props.task?.Data?.Subtasks?.length &&
    (isCurrentSubtasksVisible.value || isOtherSubtasksVisible.value),
);
/**Отображаемые подзадачи */
const visibleSubtasks = computed(() => {
  if (isCurrentSubtasksVisible.value && isOtherSubtasksVisible.value) {
    return props.task.Data.Subtasks;
  }

  return props.task.Data.Subtasks.filter((t) => {
    const isCurrent = t.CheckIsCurrentQueue(queueId.value);
    if (isCurrentSubtasksVisible.value) {
      return isCurrent;
    }
    if (isOtherSubtasksVisible.value) {
      return !isCurrent;
    }
  });
});

/**Перетаскивание элемента */
const dragComponentOnDrag = (isDragging: boolean, model: DragModel<any>) => {
  emit('dragComponentOnDrag', isDragging, model);
};
/**Элемент над которым перетаскивается */
const dragComponentOnOver = (isOver: boolean, model: DragModel<any>) => {
  emit('dragComponentOnOver', isOver, model);
};
/**Проверка разрешения наведения */
const checkAllowOver = () => {
  if (!currentDragging.value) return false;

  const item = currentDragging.value.Data;
  const ids = [item.ParentTaskId, item.Id, ...getDeepArrayIds(item.Subtasks)];
  if (ids.includes(props.task.Data.Id)) return false;

  return true;
};

/**Рекурсивное заполение id групп */
const getDeepArrayIds = (tasks: TaskModel[]) => {
  tasks?.forEach((item) => {
    if (item?.Id) {
      tempTasksIds.push(item.Id);
    }
    if (item?.Subtasks?.length) {
      getDeepArrayIds(item.Subtasks);
    }
  });
  return tempTasksIds;
};
/**Сброс узла с тасками */
const dragComponentOnDrop = (model: DragModel<TaskModel>) => {
  if (!isOver.value) return;
  emit('dragComponentOnDrop', model);
};
/**Переключение видимости задач из текущей очереди */
const toggleCurrentSubTasks = () => {
  if (!props.task.Data.Subtasks.length) emit('loadSubtasks', props.task.Data);
  isCurrentSubtasksVisible.value = !isCurrentSubtasksVisible.value;
};
/**Переключение видимости задач из других очередей */
const toggleOtherSubTasks = () => {
  if (!props.task.Data.Subtasks.length) emit('loadSubtasks', props.task.Data);
  isOtherSubtasksVisible.value = !isOtherSubtasksVisible.value;
};
</script>
<template>
  <div class="tir-pm-task-list">
    <div class="tir-wrapper-draggable" :class="{ over: isOver }">
      <TirPmDraggable
        class="drag_component"
        is-allow-click-delay
        :dragModel="task"
        :isAllowScroll="true"
        @dragging="dragComponentOnDrag"
        @over="dragComponentOnOver"
        @drop="dragComponentOnDrop"
      >
        <div class="tir-subtasks-container">
          <TaskCard
            :task="task"
            :userAccess="userAccess"
            :isTaskDragging="isTaskDragging"
            :isOver="isOver"
            :deepLevel="deepLevel"
            :nameWidth="nameWidth"
            :listColumns="listColumns"
            :dragAllowed="dragAllowed"
            :visible-task-group-data="
              visibleTaskGroupData && deepLevel >= visibleTaskGroupDataStartLevel
            "
            :groupping-type="grouppingType"
            @current="toggleCurrentSubTasks"
            @other="toggleOtherSubTasks"
            @open="emit('open', $event)"
            @removeTask="emit('removeTask', $event)"
          />
        </div>
      </TirPmDraggable>
    </div>
    <div class="tir-pm-sub-task-container" v-show="isSubtasksVisible">
      <TasksListWrapper
        :tasks="visibleSubtasks"
        :groupping-type="grouppingType"
        :deepLevel="deepLevel + 1"
        :nameWidth="nameWidth"
        :listColumns="listColumns"
        :visible-task-group-data="visibleTaskGroupData"
        :visible-task-group-data-start-level="visibleTaskGroupDataStartLevel"
        @open="emit('open', $event)"
        @dragComponentOnDrag="dragComponentOnDrag"
        @dragComponentOnOver="dragComponentOnOver"
        @dragComponentOnDrop="emit('dragComponentOnDrop', $event)"
        @loadSubtasks="emit('loadSubtasks', $event)"
      />
    </div>
  </div>
</template>
<style lang="scss">
.tir-pm-task-list {
  // *:last-child {
  //   margin-bottom: 0;
  // }

  .tir-wrapper-draggable {
    position: relative;
  }

  .tir-pm-sub-task-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: 5rem;
    margin-left: 0;
    margin-top: 0.5rem;
  }
}

.tir-subtasks-container {
  display: flex;
  align-items: center;
}

.tir-subtasks-total-info {
  position: absolute;
  top: 50%;
  right: calc(100% + 0.25rem);
  transform: translateY(-50%);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.25rem;
  text-align: right;
  padding: 0.125rem;
  font-size: 0.75rem;
  background-color: #3a7cc2;
  color: var(--neutral-0);
  cursor: pointer;

  & > svg {
    width: 1rem;
    height: 1rem;

    path {
      fill: #fff;
    }
  }
}
</style>
